import { LazyLoadImage } from "react-lazy-load-image-component";
import VideoJS from "../../../../SharedComponents/ShowMedia/VideoJS";
import RssPreview from "./RssPreview";
import { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import VideoControls from "./VideoControls";
import { setPaused } from "../../../../../redux/Slices/deviceSlice";
import { useTranslation } from "react-i18next";

const MediaPlayer = ({ data, elapsedSeconds = 0, customDuration = 10, onEnded, onProgress, areaId, forcePause = false, preloadStrategy = "metadata", isVisible = true, onLoadStart, onCanPlay, objectFitMode = "contain", cleanWrapper = false }) => {
    const { t } = useTranslation();
    const pausedSelector = useSelector((state) => state.device.paused[areaId]);
    const paused = pausedSelector || forcePause;
    const dispatch = useDispatch();
    const [showControls, setShowControls] = useState(false);


    const audioRef = useRef(null);
    const hasSeekedRef = useRef(false);
    const videoRef = useRef(null);
    // Flag: true when we paused the video ourselves due to customDuration
    // suppresses the onPause → Redux dispatch so the next item autoplays
    const pausedByDurationRef = useRef(false);

    useEffect(() => {
        if (videoRef.current) {
            if (paused) {
                videoRef.current.pause();
            } else {
                videoRef.current.play().catch((e) => {
                    console.warn("Autoplay failed:", e);
                });
            }
        }
    }, [paused]);

    const shouldMaintainAspectRatio = (data?.maintain_aspect_ratio != 0 && data?.maintain_aspect_ratio !== "0" && data?.maintain_aspect_ratio !== false);
    
    // Use 'type' as fallback when 'media_type' is undefined
    const contentType = data?.media_type || data?.type;

    // Detect media type from file extension or URL (moved to top level)
    const path = data?.path || '';
    const isImage = /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(path);
    const isVideo = /\.(mp4|webm|ogg|mov)$/i.test(path);
    const is3DModel = /\.(glb|gltf|fbx|obj)$/i.test(path);

    // Consolidated useEffect for onCanPlay triggers
    useEffect(() => {
        let timer;
        const triggerCanPlay = () => onCanPlay?.();

        if (data?.path?.includes(".m3u8")) {
            // Handled by VideoJS usually, or no specific auto-trigger needed here
        } else if (contentType == "IMAGE" || contentType == "CORNER") {
             // Trigger immediately for images
             triggerCanPlay();
        } else if (contentType == "VIDEO") {
            if (!data?.path.endsWith(".avi")) {
                // Video fallback
                timer = setTimeout(triggerCanPlay, 3000);
            }
        } else if (contentType === "YOUTUBE") {
            const videoId = new URLSearchParams(new URL(data?.path).search).get("v");
            if (videoId) {
                timer = setTimeout(triggerCanPlay, 500);
            }
        } else if (["MULTI_3D", "CHANNEL", "HDMI"].includes(contentType)) {
            triggerCanPlay();
        } else if (contentType == "3D") {
            timer = setTimeout(triggerCanPlay, 800);
        } else if (["RSS", "RSS_CUSTOM"].includes(contentType)) {
            timer = setTimeout(triggerCanPlay, 300);
        } else if (data?.original_media_type == "CLOCK" || data?.original_media_type == "WEATHER") {
            timer = setTimeout(triggerCanPlay, 500);
        } else {
             // Unrecognized specific contentType, checking fallbacks
             if (isVideo) {
                 timer = setTimeout(triggerCanPlay, 3000);
             } else if (is3DModel) {
                 timer = setTimeout(triggerCanPlay, 800);
             } else if (isImage) {
                 // No auto trigger for fallback images in original code (unlike explicit IMAGE type)
             } else {
                 // Generic iframe fallback
                 timer = setTimeout(triggerCanPlay, 500);
             }
        }

        return () => clearTimeout(timer);
    }, [data?.path, data?.media_id, data?.id, contentType, isVideo, is3DModel, isImage, data?.media_type, data?.type]);


    var html = null;

    if (data?.path?.includes(".m3u8")) {
        const play = {
            fill: !shouldMaintainAspectRatio,
            fluid: shouldMaintainAspectRatio,
            autoplay: !paused,
            controls: false,
            muted: (data?.is_muted == 1 || data?.is_muted === true || data?.is_muted === "true"),
            preload: isVisible ? "auto" : preloadStrategy,
            sources: [
                {
                    src: data?.path,
                    type: "application/x-mpegURL",
                },
            ],
        };
        html = <VideoJS {...play} />;

    } else if (contentType == "IMAGE" || contentType == "CORNER") {
        html = (
            <div className="media-previewer w-full h-full flex justify-center">
                <img 
                    src={data?.path} 
                    className="w-full h-full"
                    onLoad={() => onCanPlay?.()}
                    style={{ 
                        width: "100%", 
                        height: "100%", 
                        objectFit: shouldMaintainAspectRatio ? objectFitMode : "fill" 
                    }} 
                />
            </div>
        );

    } else if (contentType == "VIDEO") {


        if (data?.path.endsWith(".avi")) {
            html = (
                <div className="text-center mt-5">
                    <p>{t("devices.preview_browser_no_support_format")}</p>
                </div>
            );
        } else {
            
            html = (
                <div 
                    className="relative w-full h-full"
                    onMouseEnter={() => setShowControls(true)}
                    onMouseLeave={() => setShowControls(false)}
                >
                    <video
                        key={data?.media_id}
                        ref={(el) => {
                            videoRef.current = el;
                            if (el) {
                                el.onloadedmetadata = () => {
                                    const duration = el.duration;
                                    if (elapsedSeconds < duration) {
                                        el.currentTime = elapsedSeconds;
                                    } else {
                                        el.currentTime = 0;
                                    }
                                };
                            }
                        }}
                        width="100%" height="100%"
                        className="w-full h-full"
                        autoPlay={!paused}
                        muted={data?.is_muted == 1 || data?.is_muted === true || data?.is_muted === "true"}
                        preload={isVisible ? "auto" : preloadStrategy}
                        onLoadStart={() => onLoadStart?.()}
                        onCanPlay={() => onCanPlay?.()}
                        onLoadedData={() => onCanPlay?.()}
                        style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: shouldMaintainAspectRatio ? objectFitMode : "fill",
                            pointerEvents: 'none'
                        }}
                        onEnded={onEnded}
                        onPlay={() => dispatch(setPaused({ id: areaId, paused: false }))}
                        onPause={(e) => {
                            // Ignore programmatic pause triggered by our customDuration logic
                            if (pausedByDurationRef.current) {
                                pausedByDurationRef.current = false;
                                return;
                            }
                            if (!forcePause && !e.target.ended) {
                                dispatch(setPaused({ id: areaId, paused: true }));
                            }
                        }}
                        onTimeUpdate={(e) => {
                             if (onProgress && e.target.duration > 0) {
                                 onProgress((e.target.currentTime / e.target.duration) * 100);
                             }
                             // Enforce custom duration
                             if (customDuration > 0 && e.target.currentTime >= customDuration) {
                                 pausedByDurationRef.current = true; // suppress Redux dispatch
                                 e.target.pause();
                                 e.target.currentTime = 0; // reset so it restarts next time
                                 onEnded?.();
                             }
                        }}
                    >
                        <source src={data?.path} type="video/mp4" />
                        {t("devices.preview_browser_no_support_video")}
                    </video>
                    
                    <VideoControls 
                        videoRef={videoRef} 
                        isVisible={showControls}
                        customDuration={customDuration}
                    />
                </div>
            );
        }
    } else if (contentType === "YOUTUBE") {
        const videoId = new URLSearchParams(new URL(data?.path).search).get("v");
        const elapsedSecond = elapsedSeconds || 0;
        let newPath = `https://www.youtube.com/embed/${videoId}?autoplay=${!paused}&mute=${(data?.is_muted == 1 || data?.is_muted === true || data?.is_muted?.toString() === "true") ? 1 : 0}&start=${elapsedSecond}&controls=0`;
        if (!videoId) {
            html = (
                <div className="relative w-100 object-fill h-100 flex justify-content-center items-end">
                    <LazyLoadImage className="absolute" src={data?.thumb} style={{ width: "100%", height: "100%" }} />
                    <span className="absolute text-muted">{t("devices.preview_youtube_error")}</span>
                </div>
            );
        } else {
            
            html = (
                <iframe
                    className="w-full h-full"
                    style={{ 
                        width: '100%', 
                        height: '100%', 
                        objectFit: shouldMaintainAspectRatio ? objectFitMode : "fill" 
                    }}
                    allow="autoplay; encrypted-media"
                    src={newPath}
                    title="YouTube video player"
                    frameBorder="0"
                    onLoad={() => onCanPlay?.()}
                />
            );
        }
    }
    else if (contentType === "AUDIO") {
        html = (
            <audio
                key={data?.media_id}
                ref={audioRef}
                autoPlay={!paused}
                className="w-full"
                onCanPlay={() => {
                    if (audioRef.current && !hasSeekedRef.current && elapsedSeconds) {
                        audioRef.current.currentTime = elapsedSeconds;
                        hasSeekedRef.current = true;
                    }
                    onCanPlay?.();
                }}
                onEnded={() => {
                    if (customDuration && elapsedSeconds < customDuration) {
                        audioRef.current.currentTime = 0;
                        audioRef.current.play().catch(() => { });
                    }
                }}
            >
                <source src={data?.path} type="audio/ogg" />
                {t("devices.preview_browser_no_support_audio")}
            </audio>
        );
    }
    else if (["MULTI_3D"].includes(contentType)) {
        html = (
            <div className="w-100 d-flex w-100 h-100 bg-secondary text-white  fs-1 justify-content-center align-items-center">
                <span>MULTI_3D</span>
            </div>
        );
    } else if (["CHANNEL", "HDMI"].includes(contentType)) {
        html = (
            <div className="w-100 d-flex w-100 h-100 bg-secondary text-white fs-1 justify-content-center align-items-center relative">
                {data?.thumb ? (
                    <img 
                        src={data.thumb} 
                        className="w-full h-full object-fill" 
                        alt={contentType}
                        onLoad={() => onCanPlay?.()}
                    />
                ) : (
                    <span>{contentType == "CHANNEL" ? "Channel" : "HDMI"}</span>
                )}
            </div>
        );
    } else if (contentType == "3D") {
        let _path = `${process.env.REACT_APP_3D_VIEWER_URL}?id=${data?.media_id}&token=${localStorage.getItem("access_token")}`
        
        html = <iframe className="w-100" style={{ height: "100%" }} src={_path} title="3D Model Viewer" frameBorder="0" onLoad={() => onCanPlay?.()}></iframe>
    } else if (["RSS", "RSS_CUSTOM"].includes(contentType)) {
        html = <RssPreview rssSettings={data?.rss_settings} rssURL={data?.path} />;
    } else if (data?.original_media_type == "CLOCK") {
        let _path
       
            const url = new URL(data?.path);
            url.searchParams.set("id", data?.media_id);
            url.searchParams.set("token", localStorage.getItem("access_token"));
            _path = url.toString();
       
        html = <iframe className="w-100" style={{ height: "100%" }} src={_path} title="Clock Panel Preview" frameBorder="0" onLoad={() => onCanPlay?.()}></iframe>;
    }

    else if (data?.original_media_type == "WEATHER") {

        let _path
            const url = new URL(data?.path);
            url.searchParams.set("id", data?.media_id);
            url.searchParams.set("token", localStorage.getItem("access_token"));
            _path = url.toString();

        html = <iframe className="w-100" style={{ height: "100%" }} src={_path} title="Weather Panel Preview" frameBorder="0" onLoad={() => onCanPlay?.()}></iframe>;
    }
    else {
        if (isVideo) {
            html = (
                <div 
                    className="relative w-full h-full"
                    onMouseEnter={() => setShowControls(true)}
                    onMouseLeave={() => setShowControls(false)}
                >
                    <video
                        key={data?.media_id || data?.id}
                        ref={(el) => {
                            videoRef.current = el;
                            if (el) {
                                el.onloadedmetadata = () => {
                                    const duration = el.duration;
                                    if (elapsedSeconds < duration) {
                                        el.currentTime = elapsedSeconds;
                                    } else {
                                        el.currentTime = 0;
                                    }
                                };
                            }
                        }}
                        width="100%" 
                        height="100%"
                        className="w-full h-full"
                        autoPlay={!paused}
                        muted={data?.is_muted == 1 || data?.is_muted === true}
                        preload={isVisible ? "auto" : preloadStrategy}
                        onLoadStart={() => onLoadStart?.()}
                        onCanPlay={() => onCanPlay?.()}
                        onLoadedData={() => onCanPlay?.()}
                        style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: shouldMaintainAspectRatio ? objectFitMode : "fill",
                            pointerEvents: 'none'
                        }}
                        onEnded={onEnded}
                        onPlay={() => dispatch(setPaused({ id: areaId, paused: false }))}
                        onPause={(e) => {
                            // Ignore programmatic pause triggered by our customDuration logic
                            if (pausedByDurationRef.current) {
                                pausedByDurationRef.current = false;
                                return;
                            }
                            if (!forcePause && !e.target.ended) {
                                dispatch(setPaused({ id: areaId, paused: true }));
                            }
                        }}
                        onTimeUpdate={(e) => {
                             if (onProgress && e.target.duration > 0) {
                                 onProgress((e.target.currentTime / e.target.duration) * 100);
                             }
                             // Enforce custom duration
                             if (customDuration > 0 && e.target.currentTime >= customDuration) {
                                 pausedByDurationRef.current = true; // suppress Redux dispatch
                                 e.target.pause();
                                 e.target.currentTime = 0; // reset so it restarts next time
                                 onEnded?.();
                             }
                        }}
                    >
                        <source src={path} type="video/mp4" />
                        {t("devices.preview_browser_no_support_video")}
                    </video>
                    
                    <VideoControls 
                        videoRef={videoRef} 
                        isVisible={showControls}
                        customDuration={customDuration}
                    />
                </div>
            );
        } else if (is3DModel) {
            let _path = `${process.env.REACT_APP_3D_VIEWER_URL}?id=${data?.media_id}&token=${localStorage.getItem("access_token")}`
            
            html = <iframe className="w-100" style={{ height: "100%" }} src={_path} title="3D Model Viewer" frameBorder="0" onLoad={() => onCanPlay?.()}></iframe>;
        } else if (isImage) {
            html = (
                <div className="media-previewer w-full h-full flex justify-center">
                    <img 
                        src={path} 
                        className="w-full h-full"
                        style={{ 
                            width: "100%", 
                            height: "100%", 
                            objectFit: shouldMaintainAspectRatio ? objectFitMode : "fill" 
                        }} 
                        alt="Media content"
                    />
                </div>
            );
        } else {
            // Check if generic object/iframe
            html = <iframe src={path} className="w-100" style={{ height: "100%" }} title="Media content" onLoad={() => onCanPlay?.()}></iframe>;
        }
    }

    const wrapperClass = cleanWrapper 
        ? (shouldMaintainAspectRatio ? "bg-black-100 flex justify-center items-center" : "relative overflow-hidden")
        : (shouldMaintainAspectRatio ? "bg-black-100 flex justify-center items-center" : "relative overflow-hidden");

    return (
        <div
            className={`w-full h-full ${wrapperClass}`}
            style={{
                width: '100%',
                height: '100%',
            }}
        >
            {html}
        </div>
    );

}

export { MediaPlayer }